import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronRight, Languages } from 'lucide-react';

const LANGS = [
  { code: 'az', label: 'Azərbaycan' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
];

const ChallengesEditor = ({
  challenges,
  sectionTitle,
  enabled,
  onChange,
}) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [activeLang, setActiveLang] = useState('az');

  const list = Array.isArray(challenges) && challenges.length > 0
    ? challenges
    : Array.from({ length: 22 }, (_, i) => ({
        n: i + 1,
        title: { az: '', en: '', tr: '', ru: '' },
        text: { az: '', en: '', tr: '', ru: '' },
      }));

  const update = (patch) => onChange && onChange(patch);

  const updateChallenge = (idx, field, value) => {
    const next = list.map((c, i) => {
      if (i !== idx) return c;
      return {
        ...c,
        [field]: { ...(c[field] || {}), [activeLang]: value },
      };
    });
    update({ challenges: next });
  };

  const updateSectionTitle = (value) => {
    update({
      challenge_section_title: { ...(sectionTitle || {}), [activeLang]: value },
    });
  };

  return (
    <div className="admin-section" data-testid="challenges-editor-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <Sparkles size={18} className="text-yellow-400" /> Challenge-lar (Uşaqlıq Kodları)
      </h3>
      <p className="admin-section-desc">
        İ (KD1) dəyərinə uyğun 22 fərqli çətinlik şərhi. Hər biri 4 dildə (AZ/TR/EN/RU) redaktə oluna bilər.
        Doğum tarixi daxil edildikdən sonra və hesablama edildikdə İ-nin əsas dəyəri olan rəqəm üzrə uyğun
        çətinlik avtomatik olaraq AI Şərhin altında göstərilir.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => update({ challenges_enabled: e.target.checked })}
            data-testid="challenges-enabled-toggle"
          />
          <span className="text-sm text-white/85">Challenge bölməsi göstərilsin</span>
        </label>
      </div>

      {/* Language tabs */}
      <div className="flex items-center gap-2 mb-3" data-testid="challenges-lang-tabs">
        <Languages size={14} className="text-white/60" />
        {LANGS.map((L) => (
          <button
            key={L.code}
            type="button"
            onClick={() => setActiveLang(L.code)}
            className={`admin-btn-secondary text-xs ${activeLang === L.code ? 'bg-purple-500/30 border-purple-400/60' : ''}`}
            data-testid={`challenges-lang-${L.code}`}
          >
            {L.label}
          </button>
        ))}
      </div>

      {/* Section title */}
      <div className="mb-4">
        <label className="title-editor-label">Bölmənin başlığı ({activeLang.toUpperCase()})</label>
        <input
          type="text"
          className="karma-input text-left w-full"
          value={(sectionTitle && sectionTitle[activeLang]) || ''}
          onChange={(e) => updateSectionTitle(e.target.value)}
          maxLength={80}
          placeholder="Uşaqlıq Kodu / Childhood Code / Çocukluk Kodu / Код Детства"
          data-testid="challenge-section-title-input"
        />
      </div>

      {/* Challenge list */}
      <div className="space-y-2" data-testid="challenges-list">
        {list.map((ch, idx) => {
          const isOpen = expandedIdx === idx;
          const titleText = (ch.title && ch.title[activeLang]) || '';
          const bodyText = (ch.text && ch.text[activeLang]) || '';
          return (
            <div key={ch.n} className="challenge-edit-item" data-testid={`challenge-item-${ch.n}`}>
              <button
                type="button"
                onClick={() => setExpandedIdx(isOpen ? null : idx)}
                className="challenge-edit-head"
                data-testid={`challenge-toggle-${ch.n}`}
              >
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="challenge-edit-num">#{ch.n}</span>
                <span className="challenge-edit-title-preview">
                  {titleText || <span className="text-white/30">— başlıq boşdur ({activeLang})</span>}
                </span>
              </button>
              {isOpen && (
                <div className="challenge-edit-body">
                  <label className="title-editor-label">Başlıq ({activeLang.toUpperCase()})</label>
                  <input
                    type="text"
                    className="karma-input text-left w-full"
                    value={titleText}
                    onChange={(e) => updateChallenge(idx, 'title', e.target.value)}
                    maxLength={200}
                    placeholder="Challenge başlığı..."
                    data-testid={`challenge-title-input-${ch.n}`}
                  />
                  <label className="title-editor-label mt-3">Mətn ({activeLang.toUpperCase()})</label>
                  <textarea
                    className="karma-input text-left w-full"
                    value={bodyText}
                    onChange={(e) => updateChallenge(idx, 'text', e.target.value)}
                    maxLength={8000}
                    rows={10}
                    placeholder="Challenge tam mətni..."
                    data-testid={`challenge-text-input-${ch.n}`}
                    style={{ resize: 'vertical', minHeight: 200, fontFamily: 'inherit' }}
                  />
                  <span className="title-editor-hint">{bodyText.length}/8000</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChallengesEditor;
